import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useCallback, useEffect, useRef, useState } from "react";
import api from "../services/api";
import authService from "../services/auth";
import useMensagensStream from "../hooks/useMensagensStream";
import { formatDateToShow } from "../utils/formatters";
import { isSolicitacaoImutavel } from "../utils/constants";

const SCROLL_BOTTOM_THRESHOLD_PX = 32;

function ComentariosDeUmDocumento({
  open,
  onClose,
  documento,
  processo,
  onComentarioSalvo,
  tipoRecurso = "documentos",
  somenteLeitura = false,
}) {
  const [mensagens, setMensagens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [novaMensagem, setNovaMensagem] = useState("");
  const [usuarioAtualId, setUsuarioAtualId] = useState(null);
  const [erro, setErro] = useState("");
  const [novasMensagensCount, setNovasMensagensCount] = useState(0);

  const scrollContainerRef = useRef(null);
  const leituraSomente =
    somenteLeitura || isSolicitacaoImutavel(processo?.status);

  const isAtBottom = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return true;
    return (
      el.scrollHeight - el.scrollTop - el.clientHeight <=
      SCROLL_BOTTOM_THRESHOLD_PX
    );
  }, []);

  const scrollToBottom = useCallback((smooth = true) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    if (typeof el.scrollTo === "function") {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    } else {
      // Fallback (jsdom, browsers antigos): atribuição direta.
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  useEffect(() => {
    const usuario = authService.getCurrentUser();
    if (usuario?.data) {
      setUsuarioAtualId(usuario.data?.id);
    }
  }, []);

  useEffect(() => {
    if (open && documento?.id) {
      carregarMensagens();
      setNovasMensagensCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documento?.id]);

  const carregarMensagens = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/Mensagem/documento/${documento.id}`);

      if (response.data && response.data.data) {
        const mensagensOrdenadas = response.data.data.sort(
          (a, b) => new Date(a.createAt) - new Date(b.createAt),
        );
        setMensagens(mensagensOrdenadas);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Recebe nova mensagem via SSE. Deduplica por id (o autor recebe via response do POST e via SSE).
  const handleNovaMensagemStream = useCallback(
    (novaMsg) => {
      if (!novaMsg) return;
      setMensagens((prev) => {
        const idNova = novaMsg.id ?? novaMsg.Id;
        if (idNova && prev.some((m) => (m.id ?? m.Id) === idNova)) {
          return prev;
        }
        return [...prev, novaMsg];
      });

      const wasAtBottom = isAtBottom();
      // Auto-scroll silencioso quando já está no final.
      if (wasAtBottom) {
        // Aguarda render do novo item.
        requestAnimationFrame(() => scrollToBottom(true));
      } else {
        setNovasMensagensCount((c) => c + 1);
      }
    },
    [isAtBottom, scrollToBottom],
  );

  useMensagensStream({
    enabled: open && Boolean(documento?.id),
    tipoRecurso,
    recursoId: documento?.id,
    onMensagem: handleNovaMensagemStream,
  });

  const handleEnviarMensagem = async () => {
    if (leituraSomente || !novaMensagem.trim() || !usuarioAtualId || !documento?.id) {
      return;
    }

    try {
      setEnviando(true);
      setErro("");

      await api.post("/Mensagem", {
        documentoId: documento.id,
        conteudo: novaMensagem.trim(),
      });

      setNovaMensagem("");
      // SSE entrega a mensagem; carregamento explícito vira fallback caso o stream falhe.
      await carregarMensagens();
      requestAnimationFrame(() => scrollToBottom(true));

      if (onComentarioSalvo) {
        onComentarioSalvo();
      }
    } catch (error) {
      setErro(
        error.response?.data?.message ||
          "Erro ao enviar mensagem. Tente novamente.",
      );
    } finally {
      setEnviando(false);
    }
  };

  const handleClickPilula = () => {
    setNovasMensagensCount(0);
    scrollToBottom(true);
  };

  const handleScroll = () => {
    if (isAtBottom() && novasMensagensCount > 0) {
      setNovasMensagensCount(0);
    }
  };

  const agruparMensagensPorData = () => {
    const grupos = {};

    mensagens.forEach((msg) => {
      const dataFormatada = new Date(msg.createAt).toLocaleDateString("pt-BR");

      if (!grupos[dataFormatada]) {
        grupos[dataFormatada] = [];
      }

      grupos[dataFormatada].push(msg);
    });

    return grupos;
  };

  const mensagensAgrupadas = agruparMensagensPorData();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableRestoreFocus
      PaperProps={{
        sx: {
          height: "80vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle>Comentários de um Documento</DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Documento:</strong> {documento?.nome || documento?.fileName}
          </Typography>
        </Box>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        ) : mensagens.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flex: 1,
            }}
          >
            <Typography color="text.secondary">
              Nenhum comentário ainda.
            </Typography>
          </Box>
        ) : (
          <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mb: 2,
              position: "relative",
            }}
          >
            {Object.entries(mensagensAgrupadas).map(([data, msgs]) => (
              <Box key={data}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    my: 2,
                  }}
                >
                  <Divider sx={{ flex: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    {data}
                  </Typography>
                  <Divider sx={{ flex: 1 }} />
                </Box>

                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {msgs.map((msg, idx) => (
                    <Paper
                      key={msg.id ?? msg.Id ?? `${data}-${idx}`}
                      sx={{
                        p: 1.5,
                        backgroundColor:
                          msg.remetenteId === usuarioAtualId
                            ? "#E3F2FD"
                            : "#F5F5F5",
                        borderLeft:
                          msg.remetenteId === usuarioAtualId
                            ? "4px solid #2196F3"
                            : "4px solid #999",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {msg.remetenteId === usuarioAtualId
                            ? "Você"
                            : `${msg.remetenteNome || "Desconhecido"}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateToShow(msg.createAt)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {msg.conteudo}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {novasMensagensCount > 0 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 200,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
            }}
          >
            <Chip
              label={`↓ ${novasMensagensCount} ${
                novasMensagensCount === 1
                  ? "nova mensagem"
                  : "novas mensagens"
              }`}
              color="primary"
              clickable
              onClick={handleClickPilula}
              icon={<KeyboardArrowDownIcon />}
              aria-label="Ir para a mensagem mais recente"
            />
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          {leituraSomente ? (
            <Alert severity="info" sx={{ mb: 1 }}>
              Esta solicitação está em status final e não permite novos
              comentários.
            </Alert>
          ) : null}
          {erro && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {erro}
            </Alert>
          )}
          {!leituraSomente && (
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Escrever um comentário..."
              value={novaMensagem}
              onChange={(e) => setNovaMensagem(e.target.value)}
              placeholder="Digite sua mensagem aqui..."
              variant="outlined"
              disabled={enviando}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: "1px solid #eee" }}>
        <Button onClick={onClose} disabled={enviando}>
          {leituraSomente ? "Fechar" : "Cancelar"}
        </Button>
        {!leituraSomente && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleEnviarMensagem}
            disabled={!novaMensagem.trim() || enviando}
            startIcon={enviando ? <CircularProgress size={20} /> : null}
          >
            {enviando ? "Enviando..." : "Enviar"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ComentariosDeUmDocumento;
